import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  ConfirmDialog,
  ErrorBanner,
  PageHeader,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@saas-kit/ui';

import { TenantBillingPanel } from '../billing';
import {
  archiveTenant,
  getTenant,
  reactivateTenant,
  suspendTenant,
} from './api';
import { TenantDetailAudit } from './TenantDetailAudit';
import { TenantDetailOverview } from './TenantDetailOverview';
import { TenantStatusBadge } from './TenantStatusBadge';

type ConfirmKind = 'suspend' | 'reactivate' | 'archive';
type TabKey = 'overview' | 'billing' | 'audit';

export function TenantDetailPage(): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('overview');
  const [confirming, setConfirming] = useState<ConfirmKind | null>(null);

  const tenantQuery = useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => getTenant(tenantId),
    enabled: Boolean(tenantId),
  });

  async function invalidate(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-tenant', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] }),
    ]);
  }

  const suspendMutation = useMutation({
    mutationFn: () => suspendTenant(tenantId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.suspended'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });
  const reactivateMutation = useMutation({
    mutationFn: () => reactivateTenant(tenantId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.reactivated'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });
  const archiveMutation = useMutation({
    mutationFn: () => archiveTenant(tenantId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.archived'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  if (tenantQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (tenantQuery.isError || !tenantQuery.data) {
    return (
      <div className="space-y-4">
        <Link to="/tenants" className="text-sm text-primary hover:underline">
          {t('detail.back')}
        </Link>
        <ErrorBanner>{t('detail.error')}</ErrorBanner>
      </div>
    );
  }

  const tenant = tenantQuery.data;
  const isBusy =
    suspendMutation.isPending ||
    reactivateMutation.isPending ||
    archiveMutation.isPending;

  function runConfirm(): void {
    if (confirming === 'suspend') {
      suspendMutation.mutate(undefined, {
        onSuccess: () => setConfirming(null),
      });
    } else if (confirming === 'reactivate') {
      reactivateMutation.mutate(undefined, {
        onSuccess: () => setConfirming(null),
      });
    } else if (confirming === 'archive') {
      archiveMutation.mutate(undefined, {
        onSuccess: () => setConfirming(null),
      });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/tenants"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {t('detail.back')}
        </Link>
      </div>

      <PageHeader
        title={tenant.name}
        description={tenant.slug}
        actions={<TenantStatusBadge status={tenant.status} />}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="overview">{t('detail.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="billing">{t('detail.tabs.billing')}</TabsTrigger>
          <TabsTrigger value="audit">{t('detail.tabs.audit')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <TenantDetailOverview
            tenant={tenant}
            isBusy={isBusy}
            onConfirm={(kind) => setConfirming(kind)}
          />
        </TabsContent>

        <TabsContent value="billing">
          <TenantBillingPanel tenantId={tenant.id} tenantName={tenant.name} />
        </TabsContent>

        <TabsContent value="audit">
          <TenantDetailAudit tenantId={tenant.id} />
        </TabsContent>
      </Tabs>

      {confirming ? (
        <ConfirmDialog
          title={t(`confirm.${confirming}.title`, { name: tenant.name })}
          body={t(`confirm.${confirming}.body`)}
          confirmLabel={t(`actions.${confirming}`)}
          variant={confirming === 'archive' ? 'destructive' : 'primary'}
          isBusy={isBusy}
          onCancel={() => setConfirming(null)}
          onConfirm={runConfirm}
        />
      ) : null}
    </div>
  );
}
